import { integrationApi } from "@repo/api";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import AWSIntegrationForm from "../../../../integrations/src/components/aws/AWSIntegrationForm";
import { mockFormConfig } from "../../../../integrations/src/components/aws/AWSIntegrationForm/mockFormConfig";
import { useCreateIntegration } from "../../../../integrations/src/components/aws/AWSIntegrationModal/useCreateIntegration";
import { useUpdateIntegration } from "../../../../integrations/src/components/aws/AWSIntegrationModal/useUpdateIntegration";
import ModalFullPage from "../../../../integrations/src/components/ModalFullPage";
import { useIntegrationById } from "../../../../integrations/src/hooks/useIntegrationById";

/**
 * Standalone route for Add Integration page
 *
 * This route works when running integrations in standalone mode.
 * MFE mode uses its own routing, so this is only active in standalone.
 */
export const Route = createFileRoute("/integrations/add-integration")({
  component: AddIntegrationPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      id: search.id ? String(search.id) : undefined,
    };
  },
});

function AddIntegrationPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const search = useSearch({ from: "/integrations/add-integration" });
  const integrationId = search.id ? Number(search.id) : undefined;
  const isEditMode = !!integrationId;

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [cftDownloaded, setCftDownloaded] = useState(false);

  // Fetch integration data in edit mode
  const { data: integrationData } = useIntegrationById(integrationId);

  // Fetch credentials in edit mode
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) {
        throw new Error("Integration ID is required");
      }
      return await integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Set cftDownloaded to true in edit mode if credentials exist
  useEffect(() => {
    if (isEditMode && credentials && !cftDownloaded) {
      setCftDownloaded(true);
    }
  }, [isEditMode, credentials, cftDownloaded]);

  const createIntegrationMutation = useCreateIntegration({
    onSuccess: () => {
      navigate({ to: "/integrations" });
    },
    onError: (message) => {
      console.error(message);
    },
  });

  const updateIntegrationMutation = useUpdateIntegration({
    integrationId: integrationId!,
    onSuccess: () => {
      navigate({ to: "/integrations" });
    },
    onError: (message) => {
      console.error(message);
    },
  });

  const handleClose = () => {
    navigate({ to: "/integrations" });
  };

  const handleSubmit = (isEnabled: boolean) => {
    if (isEditMode) {
      updateIntegrationMutation.mutate({
        integrationType: integrationData?.integrationType || "AWS",
        isEnabled,
        data: formData,
      });
    } else {
      createIntegrationMutation.mutate({
        integrationType: "AWS",
        isEnabled,
        data: formData,
      });
    }
  };

  return (
    <ModalFullPage
      isOpen={true}
      onClose={handleClose}
      title={
        isEditMode
          ? intl.formatMessage({
              id: "Jq276c",
              defaultMessage: "Edit Integration",
              description: "Page title for edit integration form",
            })
          : intl.formatMessage({
              id: "YYujKG",
              defaultMessage: "Add Integration",
              description: "Page title for add integration form",
            })
      }
      subtitle={integrationData?.integrationType || "AWS"}
      icon={integrationData?.integrationType || "AWS"}
      primaryAction={{
        label: isEditMode
          ? intl.formatMessage({
              id: "Xpo7B8",
              defaultMessage: "Update",
              description: "Update button label",
            })
          : intl.formatMessage({
              id: "ICzs5I",
              defaultMessage: "Integrate",
              description: "Integrate button label",
            }),
        onClick: () => handleSubmit(true),
        disabled: !isFormValid,
      }}
      secondaryAction={
        isEditMode
          ? undefined
          : {
              label: intl.formatMessage({
                id: "nqp4rb",
                defaultMessage: "Save As Draft",
                description: "Save as draft button label",
              }),
              onClick: () => handleSubmit(false),
              disabled: !cftDownloaded,
            }
      }
    >
      <AWSIntegrationForm
        config={mockFormConfig}
        integrationId={integrationId}
        onClose={handleClose}
        onSubmit={(data) => {
          setFormData(data);
          setIsFormValid(Boolean(data.isValid));
          setCftDownloaded(Boolean(data.cftDownloaded));
        }}
      />
    </ModalFullPage>
  );
}
